import React, { useEffect, useState } from 'react';
import { ShieldAlert, UserPlus, FileSignature, RefreshCw, Trash2, Loader2, CheckCircle2, Copy, Link2, AlertTriangle, ScanFace } from 'lucide-react';
import { Negociacao, NegociacaoAvalista } from '../../../../types';
import { supabase } from '../../../../src/lib/supabase';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';
import { logNegotiationAction } from '../../../../src/lib/negotiationLogger';
import { logUsageEvent } from '../../../../src/lib/logger';
import AdicionarAvalistaModal from './AdicionarAvalistaModal';

interface AvalistaRecoveryPanelProps {
    negociacao: Negociacao;
    onUpdate: () => void;
}

const AVALISTA_STATUS_LABELS: Record<NegociacaoAvalista['status'], string> = {
    cadastrado: 'Avalista cadastrado',
    termo_enviado: 'Aguardando assinaturas',
    assinaturas_concluidas: 'Assinaturas concluídas',
    analise_reenviada: 'Reenviado para reanálise',
    removido: 'Removido',
};

interface SignatureLinks {
    linkTitular: string | null;
    linkAvalista: string | null;
}

// Fallback para termos gerados antes da migração 20260731120000: naquela época
// os links só existiam como texto livre em negociacao_logs.detalhes ("Titular:
// <link> | Avalista: <link>"). Desde então eles são gravados em
// negociacao_avalistas.link_titular/link_avalista, que é o que se lê primeiro.
const parseSignatureLinksFromLog = (detalhes?: string): SignatureLinks | null => {
    if (!detalhes) return null;
    const match = detalhes.match(/Titular:\s*(\S+)\s*\|\s*Avalista:\s*(\S+)/);
    if (!match) return null;
    return { linkTitular: match[1], linkAvalista: match[2] };
};

/** Bloco "Caminho de recuperação: avalista" — cadastro, reenvio de termo,
 * acompanhamento de assinaturas e reenvio para análise cadastral. Usado tanto
 * na lista de "Termos Reprovados" quanto na tela de detalhe da negociação. */
const AvalistaRecoveryPanel: React.FC<AvalistaRecoveryPanelProps> = ({ negociacao, onUpdate }) => {
    const [avalistaModalOpen, setAvalistaModalOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [signatureLinks, setSignatureLinks] = useState<SignatureLinks | null>(null);
    const [enderecoDraft, setEnderecoDraft] = useState('');
    const { user } = useAuth();
    const { addToast } = useToast();

    const avalista = negociacao.avalistas?.find(a => a.status !== 'removido');

    useEffect(() => {
        setSignatureLinks(null);
        if (avalista?.status !== 'termo_enviado') return;

        // Coluna própria é a fonte da verdade; o log só entra em cena para os
        // termos antigos, que não têm as colunas preenchidas.
        if (avalista.link_titular || avalista.link_avalista) {
            setSignatureLinks({
                linkTitular: avalista.link_titular ?? null,
                linkAvalista: avalista.link_avalista ?? null,
            });
            return;
        }

        let cancelled = false;
        (async () => {
            const { data } = await supabase
                .from('negociacao_logs')
                .select('detalhes')
                .eq('negociacao_id', negociacao.id)
                .ilike('acao', '%AVALISTA_TERMO_ENVIADO%')
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            const links = parseSignatureLinksFromLog(data?.detalhes);
            if (links && !cancelled) setSignatureLinks(links);
        })();

        return () => { cancelled = true; };
    }, [negociacao.id, avalista?.status, avalista?.link_titular, avalista?.link_avalista]);

    const handleCopyLink = (link: string) => {
        navigator.clipboard.writeText(link);
        addToast('Link copiado!', 'success');
    };

    const handleReenviarTermo = async () => {
        setActionLoading(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) throw new Error('Sessão expirada. Faça login novamente.');

            const res = await supabase.functions.invoke('gerar-termo-avalista', {
                body: { negociacao_id: negociacao.id },
            });

            if (res.error || res.data?.error) {
                // supabase.functions.invoke() retorna res.data=null em não-2xx, com o corpo
                // real da função em res.error.context (Response) — precisa ler pra pegar a
                // mensagem de verdade em vez do genérico "non-2xx status code".
                let errorMsg = res.error?.message || 'Erro ao gerar termo do avalista.';
                if (res.data?.error) {
                    errorMsg = typeof res.data.error === 'string' ? res.data.error : JSON.stringify(res.data.error);
                } else if (res.error) {
                    try {
                        const body = await (res.error as any).context?.json?.();
                        if (body?.error && typeof body.error === 'string') errorMsg = body.error;
                    } catch { /* corpo ilegível, mantém a mensagem original */ }
                }
                throw new Error(errorMsg);
            }

            if (res.data?.link_titular || res.data?.link_avalista) {
                setSignatureLinks({
                    linkTitular: res.data.link_titular ?? null,
                    linkAvalista: res.data.link_avalista ?? null,
                });
            }

            // Sucesso parcial: o termo foi criado e já está rastreado, mas faltou
            // link. Precisa aparecer como aviso, não como sucesso limpo — senão o
            // vendedor acha que enviou e o cliente nunca recebe nada.
            if (res.data?.aviso) {
                addToast(res.data.aviso, 'warning');
            } else {
                addToast('Termo com avalista enviado para assinatura!', 'success');
            }
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'gerar_termo_avalista', origem: 'acao' });
            onUpdate();
        } catch (error: any) {
            console.error('Erro ao reenviar termo com avalista:', error);
            addToast('Erro ao reenviar termo: ' + (error.message || 'Erro desconhecido'), 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReenviarAnalise = async (avalistaAtivo: NegociacaoAvalista) => {
        setActionLoading(true);
        try {
            const { data: podeReenviar, error: rpcError } = await supabase.rpc('pode_reenviar_analise_com_avalista', {
                p_negociacao_id: negociacao.id,
            });
            if (rpcError) throw rpcError;
            if (!podeReenviar) {
                addToast('Ainda há assinaturas pendentes do avalista.', 'warning');
                return;
            }

            const { error: negError } = await supabase
                .from('negociacoes')
                .update({ status: 'termo_analise' })
                .eq('id', negociacao.id);
            if (negError) throw negError;

            const { error: avalistaError } = await supabase
                .from('negociacao_avalistas')
                .update({ status: 'analise_reenviada' })
                .eq('id', avalistaAtivo.id);
            if (avalistaError) throw avalistaError;

            await logNegotiationAction(negociacao.id, user?.id, 'reenviou para validação de dados com avalista', {
                avalista: avalistaAtivo.nome_completo,
            });
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'reenviar_analise_com_avalista', origem: 'acao' });

            addToast('Negociação reenviada para validação de dados!', 'success');
            onUpdate();
        } catch (error: any) {
            console.error('Erro ao reenviar para análise com avalista:', error);
            addToast('Erro ao reenviar: ' + (error.message || 'Erro desconhecido'), 'error');
        } finally {
            setActionLoading(false);
        }
    };

    // Avalista cadastrado antes do campo existir (#6368): o termo não sai sem endereço,
    // então ele é completado aqui mesmo, sem precisar remover e cadastrar de novo.
    const handleSalvarEndereco = async (avalistaAtivo: NegociacaoAvalista) => {
        const endereco = enderecoDraft.trim();
        if (!endereco) {
            addToast('Informe o endereço completo do avalista.', 'warning');
            return;
        }
        setActionLoading(true);
        try {
            const { error } = await supabase
                .from('negociacao_avalistas')
                .update({ endereco })
                .eq('id', avalistaAtivo.id);
            if (error) throw error;
            await logNegotiationAction(negociacao.id, user?.id, 'informou endereço do avalista', { avalista: avalistaAtivo.nome_completo });
            addToast('Endereço do avalista salvo.', 'success');
            setEnderecoDraft('');
            onUpdate();
        } catch (error: any) {
            console.error('Erro ao salvar endereço do avalista:', error);
            addToast('Erro ao salvar endereço: ' + (error.message || 'Erro desconhecido'), 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRemoverAvalista = async (avalistaAtivo: NegociacaoAvalista) => {
        if (!window.confirm(`Remover o avalista ${avalistaAtivo.nome_completo}? Você poderá cadastrar outro em seguida.`)) return;
        setActionLoading(true);
        try {
            const { error } = await supabase
                .from('negociacao_avalistas')
                .update({ status: 'removido' })
                .eq('id', avalistaAtivo.id);
            if (error) throw error;

            await logNegotiationAction(negociacao.id, user?.id, 'removeu avalista', { avalista: avalistaAtivo.nome_completo });
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'remover_avalista', origem: 'acao' });
            addToast('Avalista removido.', 'success');
            onUpdate();
        } catch (error: any) {
            console.error('Erro ao remover avalista:', error);
            addToast('Erro ao remover avalista: ' + (error.message || 'Erro desconhecido'), 'error');
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <>
            <div className="p-3 rounded-xl bg-teal-50 dark:bg-teal-900/10 border border-teal-200/60 dark:border-teal-800/40 space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <ShieldAlert size={13} className="text-teal-700" />
                        <span className="text-xs font-bold text-teal-800 dark:text-teal-300 uppercase tracking-wider">
                            Caminho de recuperação: avalista
                        </span>
                    </div>
                    {avalista && (
                        <span className="text-[11px] font-semibold text-teal-700 dark:text-teal-400">
                            {AVALISTA_STATUS_LABELS[avalista.status]}
                        </span>
                    )}
                </div>

                {!avalista && (
                    <button
                        onClick={() => setAvalistaModalOpen(true)}
                        className="flex items-center gap-1.5 text-xs font-bold text-teal-700 dark:text-teal-400 hover:underline"
                    >
                        <UserPlus size={13} />
                        Adicionar avalista
                    </button>
                )}

                {avalista && (
                    <div className="space-y-1.5">
                        <p className="text-sm text-teal-900 dark:text-teal-200">
                            {avalista.nome_completo} — CPF {avalista.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}
                        </p>
                        {avalista.endereco && (
                            <p className="text-xs text-teal-800/80 dark:text-teal-300/80">{avalista.endereco}</p>
                        )}

                        {avalista.status === 'cadastrado' && !avalista.endereco?.trim() && (
                            <div className="space-y-1.5">
                                <p className="text-xs text-amber-700 dark:text-amber-400">
                                    Falta o endereço do avalista — ele sai no Termo de Anuência junto com o CPF e o e-mail.
                                </p>
                                <textarea
                                    value={enderecoDraft}
                                    onChange={(e) => setEnderecoDraft(e.target.value)}
                                    rows={2}
                                    aria-label="Endereço completo do avalista"
                                    placeholder="Rua, número, bairro, cidade/UF, CEP"
                                    className="w-full border border-teal-200 dark:border-teal-800/60 rounded-lg px-2.5 py-1.5 text-xs focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-white dark:bg-slate-900 dark:text-white resize-none"
                                />
                                <button
                                    onClick={() => handleSalvarEndereco(avalista)}
                                    disabled={actionLoading}
                                    className="flex items-center gap-1.5 text-xs font-bold text-teal-700 dark:text-teal-400 hover:underline disabled:opacity-60"
                                >
                                    {actionLoading && <Loader2 size={13} className="animate-spin" />}
                                    Salvar endereço
                                </button>
                            </div>
                        )}

                        {avalista.status === 'cadastrado' && !!avalista.endereco?.trim() && (
                            <button
                                onClick={handleReenviarTermo}
                                disabled={actionLoading}
                                className="flex items-center gap-1.5 text-xs font-bold text-teal-700 dark:text-teal-400 hover:underline disabled:opacity-60"
                            >
                                {actionLoading ? <Loader2 size={13} className="animate-spin" /> : <FileSignature size={13} />}
                                Reenviar termo com avalista
                            </button>
                        )}

                        {avalista.status === 'termo_enviado' && (
                            <div className="space-y-1.5">
                                {/* A validação facial (SERPRO) do avalista cria estados que não são
                                    "assinou / não assinou" — mostrar o estado real evita que o vendedor
                                    fique cobrando uma assinatura que já foi feita, ou ache que está tudo
                                    certo quando a biometria foi reprovada. */}
                                {avalista.avalista_biometria_reprovada_em ? (
                                    <div className="flex items-start gap-2 p-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50">
                                        <AlertTriangle size={13} className="text-red-600 mt-0.5 shrink-0" />
                                        <p className="text-xs text-red-700 dark:text-red-400">
                                            <strong>Validação facial reprovada pelo SERPRO</strong> em {new Date(avalista.avalista_biometria_reprovada_em).toLocaleDateString('pt-BR')}.
                                            A assinatura do avalista não vale — remova e cadastre novamente para gerar um novo termo.
                                        </p>
                                    </div>
                                ) : avalista.avalista_assinatura_pendente_em && !avalista.avalista_assinou_em ? (
                                    <div className="flex items-start gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50">
                                        <ScanFace size={13} className="text-amber-600 mt-0.5 shrink-0" />
                                        <p className="text-xs text-amber-700 dark:text-amber-400">
                                            Avalista assinou em {new Date(avalista.avalista_assinatura_pendente_em).toLocaleDateString('pt-BR')} — <strong>aguardando validação facial do SERPRO</strong>.
                                            {!avalista.titular_assinou_em && ' O titular ainda não assinou.'}
                                        </p>
                                    </div>
                                ) : (
                                    <p className="text-xs text-teal-700 dark:text-teal-400">
                                        Aguardando assinatura {avalista.titular_assinou_em ? 'do avalista' : avalista.avalista_assinou_em ? 'do titular' : 'de titular e avalista'}.
                                    </p>
                                )}
                                {signatureLinks ? (
                                    <div className="space-y-1">
                                        {[
                                            { label: 'Link do titular', url: signatureLinks.linkTitular, situacao: avalista.titular_assinou_em ? ' (assinado)' : '' },
                                            {
                                                label: 'Link do avalista',
                                                url: signatureLinks.linkAvalista,
                                                situacao: avalista.avalista_biometria_reprovada_em
                                                    ? ' (facial reprovada)'
                                                    : avalista.avalista_assinou_em
                                                        ? ' (assinado)'
                                                        : avalista.avalista_assinatura_pendente_em
                                                            ? ' (aguardando facial)'
                                                            : '',
                                            },
                                        ].map(({ label, url, situacao }) => (
                                            <div key={label} className="flex items-center gap-2 bg-white dark:bg-slate-900 border border-teal-200/60 dark:border-teal-800/40 rounded-lg px-2.5 py-1.5">
                                                <Link2 size={12} className={`shrink-0 ${url ? 'text-teal-600' : 'text-amber-500'}`} />
                                                <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 shrink-0">{label}{situacao}:</span>
                                                {/* O termo pode existir no Autentique sem o link ter voltado na
                                                    resposta. Dizer isso é melhor que sumir com a linha: o documento
                                                    é real e o link está lá, só precisa ser copiado do painel. */}
                                                {url ? (
                                                    <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate flex-1">{url}</span>
                                                ) : (
                                                    <span className="text-[11px] text-amber-600 dark:text-amber-400 flex-1">
                                                        não retornado — copie no painel do Autentique
                                                    </span>
                                                )}
                                                {url && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCopyLink(url)}
                                                        className="shrink-0 text-teal-600 hover:text-teal-800 dark:hover:text-teal-300"
                                                        title="Copiar link"
                                                    >
                                                        <Copy size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-[11px] text-teal-600/70 dark:text-teal-500/70">Carregando links...</p>
                                )}
                            </div>
                        )}

                        {avalista.status === 'assinaturas_concluidas' && (
                            <button
                                onClick={() => handleReenviarAnalise(avalista)}
                                disabled={actionLoading}
                                className="flex items-center gap-1.5 text-xs font-bold text-teal-700 dark:text-teal-400 hover:underline disabled:opacity-60"
                            >
                                {actionLoading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                                Reenviar para validação de dados
                            </button>
                        )}

                        {avalista.status === 'analise_reenviada' && (
                            <p className="flex items-center gap-1.5 text-xs font-bold text-teal-700 dark:text-teal-400">
                                <CheckCircle2 size={13} />
                                Reenviado para reanálise
                            </p>
                        )}

                        {avalista.status !== 'analise_reenviada' && (
                            <button
                                onClick={() => handleRemoverAvalista(avalista)}
                                disabled={actionLoading}
                                className="flex items-center gap-1.5 text-xs font-semibold text-red-600 dark:text-red-400 hover:underline disabled:opacity-60"
                            >
                                <Trash2 size={13} />
                                Remover
                            </button>
                        )}
                    </div>
                )}
            </div>

            {avalistaModalOpen && (
                <AdicionarAvalistaModal
                    isOpen={true}
                    negotiation={negociacao}
                    onClose={() => setAvalistaModalOpen(false)}
                    onSaved={() => { setAvalistaModalOpen(false); onUpdate(); }}
                />
            )}
        </>
    );
};

export default AvalistaRecoveryPanel;
